import { useEffect, useMemo, useState } from 'react'
import { Breadcrumb, Button, Divider, Dropdown, Grid, Header, Icon, Label, Loader, Segment, Table } from 'semantic-ui-react'
import moment from 'moment'
import 'moment/locale/es'
import { connect } from 'react-redux'
import superAdminBillingActions from '../../store/actions/super_admin_billing'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const PartnerBillingIndividualPage = ({ match, getSuperAdminBillingIndividual, getSuperAdminBillingByPeriod }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const superAdminId = match?.params?.id
  const [billingPeriods, setBillingPeriods] = useState([])
  const [currentBillingPeriod, setCurrentBillingPeriod] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const [isFetchingPeriod, setIsFetchingPeriod] = useState(false)
  const [superAdminData, setSuperAdminData] = useState(null)
  const [billingConfig, setBillingConfig] = useState(null)
  const [currentBillingData, setCurrentBillingData] = useState(null)

  const sections = useMemo(() => [
    { key: 'partners', content: t('Facturación de Socios'), link: true, onClick: () => history.push('/partner_billing') },
    { key: 'partner_billing', content: superAdminData?.name || t('Cargando...'), link: false }
  ], [history, superAdminData, t])

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!superAdminId) return

      setIsFetching(true)
      setSuperAdminData(null)
      setBillingConfig(null)
      setBillingPeriods([])
      setCurrentBillingPeriod(null)
      setCurrentBillingData(null)
      try {
        const data = await getSuperAdminBillingIndividual(superAdminId)

        if (data?.superAdmin) {
          setSuperAdminData(data.superAdmin)
        }

        if (data?.billingConfig) {
          setBillingConfig(data.billingConfig)
        }

        if (data?.billings && data.billings.length > 0) {
          const periods = data.billings.map(billing => {
            const date = moment().year(billing.year).month(billing.month - 1)
            return {
              key: date.format('YYYY-MM'),
              text: date.format('MMMM YYYY').charAt(0).toUpperCase() + date.format('MMMM YYYY').slice(1),
              value: date.format('YYYY-MM')
            }
          })
          setBillingPeriods(periods)

          if (periods.length > 0) {
            setCurrentBillingPeriod(periods[0].value)
          }
        }
      } finally {
        setIsFetching(false)
      }
    }
    fetchInitialData()
  }, [superAdminId])

  useEffect(() => {
    if (!currentBillingPeriod || !superAdminId) return

    const fetchBillingPeriodData = async () => {
      setIsFetchingPeriod(true)
      try {
        const data = await getSuperAdminBillingByPeriod(superAdminId, currentBillingPeriod)
        setCurrentBillingData(data?.billing || null)
      } finally {
        setIsFetchingPeriod(false)
      }
    }
    fetchBillingPeriodData()
  }, [currentBillingPeriod, superAdminId])

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$ —'
    return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
  }

  const billingDetailItems = [
    { key: 'clients', label: t('Clientes'), field: 'clients_max', priceField: 'price_per_unit' },
    { key: 'users', label: t('Usuarios'), field: 'users_max', priceField: 'price_per_unit' },
    { key: 'units', label: t('Unidades'), field: 'units_max', priceField: 'price_per_unit' },
    { key: 'trailers', label: t('Remolques'), field: 'trucks_max', priceField: 'price_per_unit' },
    { key: 'forms', label: t('Formularios'), field: 'forms_max', priceField: 'price_per_unit' },
    { key: 'answered_forms', label: t('Formularios Respondidos'), field: 'forms_res_max', priceField: 'price_per_unit' },
    { key: 'inspections', label: t('Inspecciones'), field: 'inspections_max', priceField: 'price_per_unit' },
    { key: 'answered_inspections', label: t('Inspecciones Respondidas'), field: 'inspections_res_max', priceField: 'price_per_unit' }
  ]

  return (
    <div style={{ padding: 20 }}>
      <Breadcrumb icon='right angle' sections={sections} />

      <Header as='h1' size='medium'>
        {t('Facturación de')} {superAdminData?.name || t('Cargando...')}
      </Header>

      {isFetching ? (
        <Segment>
          <Loader active inline='centered'>
            {t('Cargando información del socio...')}
          </Loader>
        </Segment>
      ) : (
        <>
          {billingConfig && (
            <Segment>
              <Header as='h3' size='small'>
                <Icon name='info circle' />
                {t('Información de Configuración')}
              </Header>
              <p>
                <strong>{t('Día de facturación:')}</strong> {billingConfig.billing_days} {t('de cada mes')}
              </p>
            </Segment>
          )}

          <Segment>
            <Grid>
              <Grid.Row>
                <Grid.Column width={4}>
                  <div>
                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{t('Período')}</p>
                    <Dropdown
                      placeholder={t('Selecciona un período')}
                      fluid
                      selection
                      options={billingPeriods}
                      value={currentBillingPeriod}
                      onChange={(e, { value }) => setCurrentBillingPeriod(value)}
                      loading={isFetchingPeriod}
                      disabled={isFetchingPeriod || billingPeriods.length === 0}
                      noResultsMessage={t('No hay períodos de facturación disponibles')}
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>

          {isFetchingPeriod ? (
            <Segment>
              <Loader active inline='centered'>
                {t('Cargando datos de facturación...')}
              </Loader>
            </Segment>
          ) : currentBillingData ? (
            <>
              <Segment>
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Header as='h2' size='large' textAlign='center'>
                        {t('Total a Pagar')}
                        <Header.Subheader style={{ marginTop: 10, fontSize: '2em', color: '#2185d0' }}>
                          {formatCurrency(currentBillingData.amount)}
                        </Header.Subheader>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>

              <Segment>
                <Header as='h3' size='small'>
                  <Icon name='list' />
                  {t('Desglose de Facturación')}
                </Header>
                <Divider />
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{t('Concepto')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign='center'>{t('Cantidad')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign='center'>{t('Precio Unitario')}</Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>{t('Subtotal')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {billingDetailItems.map(item => {
                      const detail = currentBillingData.details?.[item.key]
                      if (!detail) return null

                      const quantity = detail[item.field] || 0
                      const unitPrice = detail.price_per_unit || 0
                      const subtotal = detail.total || 0

                      return (
                        <Table.Row key={item.key}>
                          <Table.Cell>
                            <strong>{item.label}</strong>
                          </Table.Cell>
                          <Table.Cell textAlign='center'>
                            <Label color='blue'>{quantity}</Label>
                          </Table.Cell>
                          <Table.Cell textAlign='center'>
                            {formatCurrency(unitPrice)}
                          </Table.Cell>
                          <Table.Cell textAlign='right'>
                            <strong>{formatCurrency(subtotal)}</strong>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                  <Table.Footer>
                    <Table.Row>
                      <Table.HeaderCell colSpan='3' textAlign='right'>
                        <strong>{t('Total:')}</strong>
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign='right'>
                        <strong style={{ fontSize: '1.2em', color: '#2185d0' }}>
                          {formatCurrency(currentBillingData.amount)}
                        </strong>
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Footer>
                </Table>

                <div style={{ marginTop: 20, padding: 10, backgroundColor: '#f8f9fa', borderRadius: 5 }}>
                  <p style={{ margin: 0, fontSize: '0.9em', color: '#666' }}>
                    <Icon name='calendar' />
                    <strong>{t('Fecha de registro:')}</strong>{' '}
                    {moment(currentBillingData.createdAt).format('DD [de] MMMM [de] YYYY [a las] HH:mm:ss')}
                  </p>
                </div>
              </Segment>
            </>
          ) : (
            <Segment placeholder>
              <Header icon>
                <Icon name='search' />
                {t('No hay datos de facturación disponibles para el período seleccionado')}
              </Header>
            </Segment>
          )}
        </>
      )}
    </div>
  )
}

const mapDispatchToProps = (dispatch) => ({
  getSuperAdminBillingIndividual: (superAdminId) => dispatch(superAdminBillingActions.getSuperAdminBillingIndividual(superAdminId)),
  getSuperAdminBillingByPeriod: (superAdminId, period) => dispatch(superAdminBillingActions.getSuperAdminBillingByPeriod(superAdminId, period))
})

export default connect(null, mapDispatchToProps)(PartnerBillingIndividualPage)